import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import { colors } from "app/popleads/components/ui-kit/colors";
import SelectableTaskCard from "app/popleads/components/ui-kit/SelectableTaskCard";
import DateHeader from "app/popleads/components/ui-kit/DateHeader";
import moment from "moment";
import { StyledFooterButton } from "./ExtensionNewTask";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import Switch from "app/popleads/components/ui-kit/Switch";
import useCompletedTasks from "app/popleads/hooks/useGetCompletedTasks";
import useTaskDelete from "app/popleads/hooks/useTaskDelete";
import useTaskComplete from "app/popleads/hooks/useTaskComplete";
import useUpdateTasksTime from "app/popleads/hooks/useUpdateTasksTime";
import { useExtensionContext } from "../ExtensionContext";
import { ExtensionCardHeader } from "app/popleads/components/ui-kit/ExtensionCard";
import { POPUP_CONTENTS } from "../utils/constants";
import { toast } from "react-toastify";
import CustomDatePicker from "app/popleads/components/ui-kit/DateInput";
import Modal from "app/popleads/components/ui-kit/Modal";

const TaskListContainer = styled.div`
    height: calc(100% - 107px);
    position: relative;
    display: flex;
    flex-direction: column;
`;

const DateHeaderContainer = styled.div`
    position: sticky;
    top: 0;
    background-color: ${colors.neutral[50]};
    z-index: 10;
    padding: 12px 0;
    padding: 0 12px;
`;

const SelectAllContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background-color: ${colors.neutral[50]};
    border-bottom: 1px solid ${colors.neutral[200]};
`;

const SelectAllCheckbox = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid ${(props) => (props.checked ? colors.primary[900] : colors.neutral[500])};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: ${(props) => (props.checked ? colors.primary[900] : "transparent")};
    transition: all 0.2s ease;
    margin: 0;

    &:after {
        content: ${(props) => (props.checked ? "'✓'" : "''")};
        color: ${colors.white};
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
    }
`;

const SelectAllLabel = styled.label`
    color: ${colors.neutral[900]};
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    margin: 0;
    line-height: 16px;
`;

const BulkActionsContainer = styled.div`
    display: flex;
    justify-content: space-around;
    align-items: center;
    gap: 8px;
`;

const TasksContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 0 12px;
    padding-bottom: 10px;
`;

const StyledButtonContainer = styled(StyledFooterButton)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    position: absolute;
    width: inherit;
`;

const BulkActionsFooter = styled.div`
    position: absolute;
    bottom: 58px;
    left: 0;
    right: 0;
    background-color: ${colors.white};
    border-top: 1px solid ${colors.neutral[200]};
    z-index: 10;
`;

const DashedDivider = styled.hr`
    border: none;
    border-top: 1px dashed ${colors.neutral[200]};
    margin: 16px 0 8px 0;
`;

const TimeDisplay = styled.div`
    color: ${colors.neutral[700]};
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px;
`;

const NoTasksMessage = styled.div`
    text-align: center;
    padding: 20px;
    color: ${colors.neutral[500]};
`;

const StyledText = styled.div`
    color: ${colors.neutral[900]};
    font-size: 14px;
`;
const StyledFlex = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;
const StyledTaskContainer = styled.div`
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 20px;
    }
`;

const TimeGroup = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderTitle = styled.div`
    color: ${colors.neutral[900]};
    font-size: 18px;
    font-weight: 500;
`;

const MoveButtonWrapper = styled.div`
    position: relative;
    padding: 4px 0;
    overflow: visible;

    &:hover .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown:hover {
        display: ${(props) => (props.showDatePicker ? "none" : "block")};
    }

    .move-dropdown {
        display: ${(props) => (props.showDatePicker ? "none !important" : "none")};
    }
`;

const MoveDropdown = styled.div`
    position: absolute;
    background: ${colors.white};
    border: 1px solid ${colors.neutral[100]};
    border-radius: 6px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    padding: 8px 0;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    display: none;
    min-width: 100px;
    z-index: 1000;

    &:after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid ${colors.white};
        z-index: 2;
    }

    &:before {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 9px solid transparent;
        border-right: 9px solid transparent;
        border-top: 9px solid ${colors.neutral[100]};
        z-index: 1;
        margin-top: 1px;
    }
`;

const MoveOption = styled.div`
    padding: 8px 16px;
    color: ${colors.neutral[800]};
    font-size: 14px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral[50]};
    }
`;

const DatePickerWrapper = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 99999;
    background: ${colors.white};
    border-radius: 8px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
    border: 1px solid ${colors.neutral[200]};
`;

const ExtensionTaskList = ({ taskList, taskListLoading, setTaskToDelete }) => {
    const { getCompletedTasks } = useCompletedTasks();
    const { bulkDeleteTasks, loading: deleteLoading } = useTaskDelete();
    const { bulkCompleteTasks, loading: completeLoading } = useTaskComplete();
    const { updateTasksTime, loading: moveLoading } = useUpdateTasksTime();
    const {
        selectedDate,
        setState,
        liveTasks,
        setLiveTasks,
        selectedTasksId,
        staticTaskList,
        fetchStaticTaskList,
        modals,
        handleBulkDelete: contextBulkDelete,
        selectedModal,
        modalMessage,
    } = useExtensionContext();
    const [tasks, setTasks] = useState([]);
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [showOverdueTasks, setShowOverdueTasks] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const taskRefs = useRef({});
    const tasksContainerRef = useRef(null);

    const selectedDayStart = moment(selectedDate)
        .startOf("day")
        .valueOf();
    const selectedDayEnd = moment(selectedDate)
        .endOf("day")
        .valueOf();

    useEffect(() => {
        fetchStaticTaskList();
    }, [fetchStaticTaskList]);

    useEffect(() => {
        setLiveTasks([]);
    }, [selectedDate]);

    useEffect(() => {
        if (selectedTasksId && taskRefs.current[selectedTasksId] && tasksContainerRef.current) {
            setTimeout(() => {
                const taskElement = taskRefs.current[selectedTasksId];
                const container = tasksContainerRef.current;

                if (!taskElement || !container) return;

                try {
                    const containerRect = container.getBoundingClientRect();
                    const taskRect = taskElement.getBoundingClientRect();

                    if (containerRect.height === 0 || taskRect.height === 0) return;

                    const scrollTop =
                        container.scrollTop +
                        taskRect.top -
                        containerRect.top -
                        containerRect.height / 2 +
                        taskRect.height / 2;

                    container.scrollTo({
                        top: Math.max(0, scrollTop),
                        behavior: "smooth",
                    });
                } catch (error) {
                    toast.error("Scroll to task failed");
                }
            }, 100);
        }
    }, [selectedTasksId, tasks]);

    useEffect(() => {
        const handleTasks = async () => {
            if (isActive) {
                const completedTasksData = await getCompletedTasks({ selectedDate });
                if (completedTasksData?.completedTasks?.length > 0) {
                    const sortedTasks = sortTasksByTime(completedTasksData.completedTasks);
                    setTasks(sortedTasks);
                    setSelectedTasks(sortedTasks.map((task) => task.id));
                } else {
                    setTasks([]);
                    setSelectedTasks([]);
                }
            } else if (showOverdueTasks && taskList?.overdueTasks?.length > 0) {
                const sortedTasks = sortTasksByTime(taskList.overdueTasks);
                setTasks(sortedTasks);
                setSelectedTasks([]);
            } else if (taskList?.gorev?.length > 0) {
                const sortedTasks = sortTasksByTime(taskList.gorev);
                setTasks(sortedTasks);
                setSelectedTasks([]);
            } else {
                setTasks([]);
                setSelectedTasks([]);
            }
        };

        handleTasks();
    }, [isActive, selectedDate, taskList?.gorev, taskList?.overdueTasks, showOverdueTasks, taskListLoading]);

    useEffect(() => {
        if (liveTasks.length > 0) {
            const filteredLiveTasks = liveTasks.filter(
                (task) => task.zaman >= selectedDayStart && task.zaman <= selectedDayEnd
            );
            if (filteredLiveTasks.length > 0) {
                const findTask = taskList?.gorev?.some((task) => task.id === filteredLiveTasks[0].id);
                if (!findTask) {
                    const newTasks = [...tasks, ...filteredLiveTasks];
                    const sortedTasks = sortTasksByTime(newTasks);
                    setTasks(sortedTasks);
                }
            }
            setLiveTasks([]);
        }
    }, [tasks, liveTasks]);

    const handleTaskCompletion = (taskId) => {
        if (taskId === null) {
            setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
            return;
        }

        setSelectedTasks((prev) => {
            if (prev.includes(taskId)) {
                return prev.filter((id) => id !== taskId);
            } else {
                return [...prev, taskId];
            }
        });
    };

    const sortTasksByTime = (tasksToSort) => {
        return [...tasksToSort].sort((a, b) => {
            const timeA = moment(a.zaman).format("HH:mm");
            const timeB = moment(b.zaman).format("HH:mm");

            const [hoursA, minutesA] = timeA.split(":").map(Number);
            const [hoursB, minutesB] = timeB.split(":").map(Number);

            const timeAMinutes = hoursA * 60 + minutesA;
            const timeBMinutes = hoursB * 60 + minutesB;

            return timeAMinutes - timeBMinutes;
        });
    };

    const groupTasksByTime = (tasks) => {
        const groups = {};
        tasks.forEach((task) => {
            const timeKey = showOverdueTasks
                ? moment(task.zaman).format("MMM DD, YYYY - hh:mm A")
                : moment(task.zaman).format("hh:mm A");

            if (!groups[timeKey]) {
                groups[timeKey] = [];
            }
            groups[timeKey].push(task);
        });
        return groups;
    };

    /*const groupTasksByDate = (tasks) => {
        const groups = {};
        tasks.forEach((task) => {
            const dateKey = moment(task.zaman).format("MMM DD, YYYY");
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(task);
        });
        return groups;
    };*/

    const handleShowOverdueTasks = () => {
        setShowOverdueTasks(true);
        setIsActive(false);
    };

    const handleShowTodayTasks = () => {
        setShowOverdueTasks(false);
        setIsActive(false);
        setState({ selectedDate: new Date() });
    };

    const highlightedDates = useMemo(() => {
        if (!staticTaskList?.gorev && !staticTaskList?.overdueTasks) return [];

        const dates = [];

        if (staticTaskList?.gorev) {
            staticTaskList.gorev.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        if (staticTaskList?.overdueTasks) {
            staticTaskList.overdueTasks.forEach((task) => {
                const date = moment(task?.zaman).format("YYYY-MM-DD");
                if (!dates.includes(date)) {
                    dates.push(date);
                }
            });
        }

        return dates;
    }, [staticTaskList?.gorev, staticTaskList?.overdueTasks]);

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        if (newSelectAll) {
            const allTaskIds = tasks.map((task) => task.id);
            setSelectedTasks(allTaskIds);
        } else {
            setSelectedTasks([]);
        }
    };

    const handleDelete = () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to delete");
            return;
        }

        setState({
            selectedModal: modals.DELETE_BULK_TASKS,
            modalMessage: "Are you sure want to delete all selected task?",
        });
    };

    const handleBulkDelete = async () => {
        const onSuccess = () => {
            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);
        };

        await contextBulkDelete(selectedTasks, bulkDeleteTasks, onSuccess);
    };

    const handleComplete = async () => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to complete");
            return;
        }

        try {
            await bulkCompleteTasks(selectedTasks);

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) completed successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error("Failed to complete tasks");
        }
    };

    const handleMove = async (days) => {
        if (selectedTasks.length === 0) {
            toast.warning("Please select tasks to move");
            return;
        }

        try {
            if (days === "other") {
                setShowDatePicker(true);
                return;
            }

            await updateTasksTime(selectedTasks, { period: days });

            const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
            setTasks(remainingTasks);
            setSelectedTasks([]);
            setSelectAll(false);

            toast.success(`${selectedTasks.length} task(s) moved by ${days} day${days > 1 ? "s" : ""} successfully`);

            fetchStaticTaskList();
        } catch (error) {
            toast.error("Failed to move tasks");
        }
    };

    const handleDateChange = async (date) => {
        if (date && selectedTasks.length > 0) {
            try {
                const selectedDateMs = new Date(date).getTime();

                // Send API request to move selected tasks to specific date
                await updateTasksTime(selectedTasks, { zaman: selectedDateMs });

                // Remove moved tasks from the local task list
                const remainingTasks = tasks.filter((task) => !selectedTasks.includes(task.id));
                setTasks(remainingTasks);
                setSelectedTasks([]);
                setSelectAll(false);
                setShowDatePicker(false);

                toast.success(
                    `${selectedTasks.length} task(s) moved to ${moment(date).format("MMM DD, YYYY")} successfully`
                );

                // Optionally refetch task list to ensure consistency
                fetchStaticTaskList();
            } catch (error) {
                toast.error("Failed to move tasks");
            }
        }
    };

    // Update selectAll state based on selectedTasks
    useEffect(() => {
        if (tasks.length === 0) {
            setSelectAll(false);
        } else if (selectedTasks.length === tasks.length) {
            setSelectAll(true);
        } else {
            setSelectAll(false);
        }
    }, [selectedTasks, tasks]);

    return (
        <>
            <TaskListContainer>
                <DateHeaderContainer>
                    {showOverdueTasks ? (
                        <ExtensionCardHeader
                            cardTitle={<HeaderTitle>All Overdues</HeaderTitle>}
                            button={
                                <ButtonWithText
                                    onClick={() => {
                                        setState({
                                            popupContent: POPUP_CONTENTS.NEW_TASK,
                                            taskPage: true,
                                            editMode: false,
                                        });
                                    }}
                                    buttonStyle="default"
                                    icon="add"
                                    iconType="material"
                                    variant="primary"
                                    type="default"
                                    size="circle"
                                />
                            }
                        />
                    ) : (
                        <DateHeader highlightedDates={!taskListLoading ? highlightedDates : undefined} />
                    )}
                </DateHeaderContainer>
                {!taskListLoading && tasks.length > 0 && (
                    <>
                        <SelectAllContainer>
                            <SelectAllCheckbox
                                checked={selectAll}
                                onClick={handleSelectAll}
                                role="checkbox"
                                aria-checked={selectAll}
                            />
                            <SelectAllLabel onClick={handleSelectAll}>Select All Tasks</SelectAllLabel>
                        </SelectAllContainer>
                    </>
                )}
                <TasksContainer ref={tasksContainerRef}>
                    {taskListLoading ? (
                        <LoadingContainer>
                            <Spinner animation="border" variant="primary" />
                        </LoadingContainer>
                    ) : !tasks.length ? (
                        <NoTasksMessage>No tasks found</NoTasksMessage>
                    ) : (
                        <div>
                            {(() => {
                                let globalIndex = 0;
                                return Object.entries(groupTasksByTime(tasks)).map(([time, groupTasks]) => (
                                    <TimeGroup key={time}>
                                        <DashedDivider />
                                        <TimeDisplay>{time}</TimeDisplay>
                                        {groupTasks.map((task, idx) => {
                                            const currentGlobalIndex = globalIndex;
                                            globalIndex++;
                                            return (
                                                <StyledTaskContainer
                                                    key={task.id}
                                                    ref={(el) => {
                                                        if (el) {
                                                            taskRefs.current[task.id] = el;
                                                        }
                                                    }}
                                                >
                                                    <SelectableTaskCard
                                                        task={task}
                                                        index={currentGlobalIndex}
                                                        setTaskToDelete={setTaskToDelete}
                                                        selectedTasks={selectedTasks}
                                                        onTaskComplete={handleTaskCompletion}
                                                        overdue={
                                                            showOverdueTasks ||
                                                            (!showOverdueTasks && moment(task.zaman).isBefore(moment()))
                                                        }
                                                        greyMode={selectedTasks.length > 0}
                                                        hideBulkActions={selectedTasks.length > 1 || selectAll}
                                                    />
                                                </StyledTaskContainer>
                                            );
                                        })}
                                    </TimeGroup>
                                ));
                            })()}
                        </div>
                    )}
                </TasksContainer>
            </TaskListContainer>

            {(selectedTasks.length > 1 || selectAll) && (
                <BulkActionsFooter>
                    <BulkActionsContainer>
                        <ButtonWithText
                            size="small"
                            variant="success"
                            buttonStyle="borderless"
                            onClick={handleComplete}
                            disabled={selectedTasks.length === 0 || completeLoading}
                        >
                            {completeLoading ? "Completing..." : "Complete"}
                        </ButtonWithText>
                        <MoveButtonWrapper showDatePicker={showDatePicker}>
                            <ButtonWithText
                                size="small"
                                variant="passive"
                                buttonStyle="borderless"
                                disabled={selectedTasks.length === 0 || moveLoading}
                            >
                                {moveLoading ? "Moving..." : "Move"}
                            </ButtonWithText>
                            <MoveDropdown className="move-dropdown">
                                <MoveOption onClick={() => handleMove(1)}>+1 day</MoveOption>
                                <MoveOption onClick={() => handleMove(3)}>+3 days</MoveOption>
                                <MoveOption onClick={() => handleMove(7)}>+7 days</MoveOption>
                                <MoveOption onClick={() => handleMove("other")}>Other</MoveOption>
                            </MoveDropdown>
                            {showDatePicker && (
                                <DatePickerWrapper>
                                    <CustomDatePicker
                                        variant="move-dropdown"
                                        onChange={handleDateChange}
                                        onClickOutside={() => setShowDatePicker(false)}
                                    />
                                </DatePickerWrapper>
                            )}
                        </MoveButtonWrapper>
                        <ButtonWithText
                            size="small"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleDelete}
                            disabled={selectedTasks.length === 0 || deleteLoading}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </ButtonWithText>
                    </BulkActionsContainer>
                </BulkActionsFooter>
            )}

            <StyledButtonContainer>
                {showOverdueTasks ? (
                    <ButtonWithText
                        size="small"
                        variant="primary"
                        buttonStyle="borderless"
                        onClick={handleShowTodayTasks}
                    >
                        Today
                    </ButtonWithText>
                ) : (
                    <>
                        <ButtonWithText
                            size="small"
                            variant="danger"
                            buttonStyle="borderless"
                            onClick={handleShowOverdueTasks}
                        >
                            Show All Overdues
                        </ButtonWithText>
                        <StyledFlex>
                            <StyledText>Only Completed</StyledText>
                            <Switch
                                isActive={isActive}
                                onClick={() => {
                                    const newValue = !isActive;
                                    setIsActive(newValue);
                                    if (newValue) {
                                        setShowOverdueTasks(false);
                                    }
                                }}
                            />
                        </StyledFlex>
                    </>
                )}
            </StyledButtonContainer>
            <Modal
                isOpen={selectedModal === modals.DELETE_BULK_TASKS}
                successButtonText="Yes, Delete"
                cancelButtonText="Not Now"
                onClose={() =>
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }))
                }
                title={modalMessage || "Are you sure want to delete all selected task?"}
                variant="danger"
                icon="delete"
                onSuccess={() => {
                    handleBulkDelete();
                    setState((prevState) => ({
                        ...prevState,
                        selectedModal: null,
                    }));
                }}
            >
                <div>This action cannot be undone.</div>
            </Modal>
        </>
    );
};

export default ExtensionTaskList;
